// src/pages/customers/CustomerWizardPage.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  User,
  Briefcase,
  Users,
  BadgeCheck,
  Package,
  Camera,
  ClipboardList,
  Flag,
  ChevronLeft,
  ChevronRight,
  Save,
  Plus,
  Trash2,
  MapPin,
  ShieldCheck,
  Loader2,
} from "lucide-react";

import { apiFetch } from "@/lib/api";
import { cn } from "@/lib/utils";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { helper } from "@/lib/helper";
import { DatePickerField } from "@/components/DatePickerField";
import { toastError, toastSuccess } from "@/lib/toast";

/**
 * ✅ Routes
 * - GET  /actions/new-customer/{id}                     (id=0 create mode, else edit mode)
 * - GET  /actions/new-customer/branch-bdos/{branchId}
 *
 * ✅ Step saves
 * - POST /actions/new-customer/{id}/personal
 * - POST /actions/new-customer/{id}/business
 * - POST /actions/new-customer/{id}/kin
 * - POST /actions/new-customer/{id}/guarantors
 * - POST /actions/new-customer/{id}/assets
 * - POST /actions/new-customer/{id}/files          (multipart/form-data)
 * - POST /actions/new-customer/{id}/assessment
 * - POST /actions/new-customer/{id}/final
 */

type StepKey =
  | "personal"
  | "business"
  | "kin"
  | "referees"
  | "assets"
  | "photos"
  | "assessment"
  | "final";

const steps: Array<{ key: StepKey; label: string; icon: any }> = [
  { key: "personal", label: "Personal", icon: User },
  { key: "business", label: "Business", icon: Briefcase },
  { key: "kin", label: "Next of Kin", icon: Users },
  { key: "referees", label: "Referees", icon: BadgeCheck },
  { key: "assets", label: "Assets", icon: Package },
  { key: "photos", label: "Photos", icon: Camera },
  { key: "assessment", label: "Assessment", icon: ClipboardList },
  { key: "final", label: "Final", icon: Flag },
];

const yesNo = ["Yes", "No"] as const;



type Branch = { id: number; name: string };
type Bdo = { id: number; name: string };
type Lookup = { id: number; name: string };

type Guarantor = {
  id: number;
  guarantor_name: string;
  phone_number: string;
  national_id: string;
  RELATIONSHIP?: string;
};

type CollateralAsset = { id: number; itemDesc: string; serialNo: string; itemValue: string | number };

type ApiCustomer = any;

type ApiResponse = {
  branches: Branch[];
  gender: Lookup[];
  marital: Lookup[];
  licenced: Lookup[];
  kinRelationship: Lookup[];
  here_about_us: Lookup[];
  customer: ApiCustomer | null;
  guarantors: Guarantor[];
  guaRelationship: Lookup[];
  collateral_assets: CollateralAsset[];
  gua_relationship: Lookup[];
};

type RefereeRow = { id: string; name: string; relationship: string; phone: string; idNumber: string };
type AssetRow = { id: string; description: string; serial: string; value: string };

type PhotoKey =
  | "photo"
  | "photo1"
  | "photo2"
  | "photo3"
  | "photo4"
  | "photo5"
  | "photo6"
  | "id_side1"
  | "id_side2"
  | "home_photo"
  | "business_photo"
  | "home_visit_form"
  | "customer_signature"
  | "_other";

type PhotoSlot = { key: PhotoKey; label: string };

const photoSlots: PhotoSlot[] = [
  { key: "photo", label: "Customer Photo" },
  { key: "id_side1", label: "ID Side 1" },
  { key: "id_side2", label: "ID Side 2" },
  { key: "home_photo", label: "Home Photo" },
  { key: "business_photo", label: "Business Photo" },
  { key: "home_visit_form", label: "Home Visit Form" },
  { key: "customer_signature", label: "Customer Signature" },
  { key: "_other", label: "Other Attachment" },
  { key: "photo1", label: "Photo 1" },
  { key: "photo2", label: "Photo 2" },
  { key: "photo3", label: "Photo 3" },
  { key: "photo4", label: "Photo 4" },
  { key: "photo5", label: "Photo 5" },
  { key: "photo6", label: "Photo 6" },
];

const uid = () => Math.random().toString(36).slice(2, 10);



function Field({
  label,
  required,
  children,
  hint,
}: {
  label: string;
  required?: boolean;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-1">
        <Label className="text-sm font-medium">
          {label} {required && <span className="text-destructive">*</span>}
        </Label>
        {hint ? <span className="text-xs text-muted-foreground">{hint}</span> : null}
      </div>
      {children}
    </div>
  );
}

function SectionShell({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <Card className="shadow-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base">{title}</CardTitle>
        {subtitle ? <p className="text-sm text-muted-foreground">{subtitle}</p> : null}
      </CardHeader>
      <CardContent className="space-y-6">{children}</CardContent>
    </Card>
  );
}

/**
 * ✅ FIX: Chrome / shadcn Button inside label sometimes won't trigger file picker.
 * Use a ref to click the input directly.
 */
function PhotoTile({
  label,
  value,
  existingUrl,
  onPick,
  onClear,
}: {
  label: string;
  value?: File | null;
  existingUrl?: string | null;
  onPick: (f: File) => void;
  onClear: () => void;
}) {
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  const previewUrl = useMemo(() => {
    if (!value) return "";
    return URL.createObjectURL(value);
  }, [value]);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const showUrl = value ? previewUrl : existingUrl || "";

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden shadow-sm">
      <div className="h-40 bg-muted/40 grid place-items-center relative">
        {showUrl ? (
          <img src={showUrl} alt={label} className="h-full w-full object-cover" />
        ) : (
          <div className="text-center px-3">
            <Camera className="h-8 w-8 mx-auto text-muted-foreground" />
            <p className="mt-2 text-xs text-muted-foreground">No photo</p>
          </div>
        )}

        {/* Clear selected file (doesn't delete server file, only clears selection) */}
        {value ? (
          <Button type="button" variant="destructive" size="sm" className="absolute top-2 right-2" onClick={onClear}>
            <Trash2 className="h-4 w-4 mr-1" />
            Remove
          </Button>
        ) : null}

        {/* Open existing image */}
        {!value && existingUrl ? (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="absolute top-2 left-2"
            onClick={() => window.open(existingUrl, "_blank")}
          >
            View
          </Button>
        ) : null}
      </div>

      <div className="p-3 border-t border-border flex items-center justify-between gap-2">
        <p className="text-sm font-medium">{label}</p>

        <input
          ref={inputRef}
          type="file"
          className="hidden"
          accept="image/*"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) onPick(f);
            e.currentTarget.value = "";
          }}
        />

        <Button type="button" size="sm" className="rounded-lg" onClick={() => inputRef.current?.click()}>
          {value ? "Change" : existingUrl ? "Replace" : "Upload"}
        </Button>
      </div>
    </div>
  );
}

/** ✅ Responsive Tabs (LOCKED: can't jump ahead) */
function ResponsiveTabs({
  step,
  setStep,
  stepOk,
  maxStepReached,
  saving,
}: {
  step: number;
  setStep: (n: number) => void;
  stepOk: boolean;
  maxStepReached: number;
  saving: boolean;
}) {


  const [tabPage, setTabPage] = useState(0);

  useEffect(() => {

    setTabPage(Math.floor(step));
  }, [step,]);




  const Icon = steps[step].icon;
  return (
    <div className="flex items-center justify-between gap-2 rounded-xl border border-border bg-card px-3 py-2">
      <div className="inline-flex items-center gap-2 min-w-0">
        <span className="h-9 w-9 rounded-full bg-muted grid place-items-center">
          <Icon className="h-5 w-5 text-muted-foreground" />
        </span>
        <div className="min-w-0">
          <div className="text-sm font-semibold truncate">{steps[step].label}</div>
          <div className="text-xs text-muted-foreground">
            Step {step + 1}/{steps.length} {stepOk ? "• OK" : "• Required"}
          </div>
        </div>
      </div>

      <Badge variant="secondary" className="shrink-0">
        {step + 1}/{steps.length}
      </Badge>
    </div>
  );



}

function toNumberOrNull(v: string) {
  const x = Number(v);
  return Number.isFinite(x) ? x : null;
}

function compact<T extends Record<string, any>>(obj: T) {
  const out: any = {};
  Object.entries(obj).forEach(([k, v]) => {
    if (v === "" || v === undefined) return;
    out[k] = v;
  });
  return out as Partial<T>;
}

async function postJson<T>(url: string, body: any) {
  return apiFetch<T>(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export default function CustomerWizardPage() {
  const nav = useNavigate();
  const params = useParams();
  const location = useLocation();
  const stateCustomerId = (location.state as any)?.customerId as string | undefined;
  const urlId = params.id;

  const customerId = stateCustomerId ?? String(urlId ?? "0");
  //const customerId = String(params.id ?? "0"); // /actions/new-customer/:id
  const isEdit = customerId !== "0";

  // server lists
  const [branches, setBranches] = useState<Branch[]>([]);
  const [bdos, setBdos] = useState<Bdo[]>([]);
  const [genders, setGenders] = useState<Lookup[]>([]);
  const [maritals, setMaritals] = useState<Lookup[]>([]);
  const [licenced, setLicenced] = useState<Lookup[]>([]);
  const [kinRels, setKinRels] = useState<Lookup[]>([]);
  const [guarantorRelationship, setGuarantorRelationship] = useState<Lookup[]>([]);

  const [hereAboutUs, setHereAboutUs] = useState<Lookup[]>([]);
  // server extras (edit preview)
  const [guarantors, setGuarantors] = useState<Guarantor[]>([]);
  const [collateralAssets, setCollateralAssets] = useState<CollateralAsset[]>([]);

  // ui
  const [step, setStep] = useState(0);
  const [maxStepReached, setMaxStepReached] = useState(0); // ✅ lock steps
  const [bootLoading, setBootLoading] = useState(true);
  const [bdoLoading, setBdoLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // --- Form state ---
  const [personal, setPersonal] = useState({
    branchId: "",
    date: "",
    bdoId: "",
    dob: "",
    firstName: "",
    middleName: "",
    lastName: "",
    aka: "",
    gender: "",
    idNumber: "",
    phone: "",
    altPhone: "",
    email: "",
    marital: "",
    dependents: "",
    homeAddress: "",
    town: "",
    landmark: "",
    estate: "",
    houseNumber: "",
    ownership: "",
    rentAmount: "",
    rentSince: "",
    assetValue: "",
    hearAbout: "",
    homeLat: "",
    homeLng: "",
  });

  const [business, setBusiness] = useState({
    name: "",
    yearStarted: "",
    weeklyStockSpend: "",
    transport: "",
    type: "",
    employees: "",
    weeklyGrossProfit: "",
    weeklyChamaContribution: "",
    town: "",
    licensed: "",
    weeklyExpenses: "",
    chamaMembers: "",
    street: "",
    licenseNumber: "",
    weeklyNetProfit: "",
    chamaPayout: "",
    buildingName: "",
    currentStockValue: "",
    rentAmount: "",
    buildingOwnership: "",
    weeklySales: "",
    utilities: "",
    address: "",
    bizLat: "",
    bizLng: "",
  });

  const [kin, setKin] = useState({ name: "", phone: "", town: "", relationship: "" });

  // ✅ REQUIRE EXACTLY 5 referees
  const REQUIRED_REFEREES = 4;
  const [referees, setReferees] = useState<RefereeRow[]>(
    Array.from({ length: REQUIRED_REFEREES }).map(() => ({
      id: uid(),
      name: "",
      relationship: "",
      phone: "",
      idNumber: "",
    }))
  );

  const [assets, setAssets] = useState<AssetRow[]>([{ id: uid(), description: "", serial: "", value: "" }]);

  const [photos, setPhotos] = useState<Record<PhotoKey, File | null>>(() => {
    const base = {} as Record<PhotoKey, File | null>;
    photoSlots.forEach((s) => (base[s.key] = null));
    return base;
  });

  type ExistingPhotoUrls = Partial<Record<PhotoKey, string>>;

  const [existingPhotoUrls, setExistingPhotoUrls] = useState<ExistingPhotoUrls>({});

  const [assessment, setAssessment] = useState({
    stockNeat: "Yes",
    ledgerAccurate: "Yes",
    salesActivity: "Yes",
    permanent: "Yes",
    proofOwnership: "Yes",
    forthComing: "Yes",
    knownToMarket: "Yes",
    soundReputation: "Yes",
  });

  const [finalRemarks, setFinalRemarks] = useState("");

  // Boot: GET /actions/new-customer/{id}
  useEffect(() => {
    let mounted = true;

    async function boot() {
      setBootLoading(true);
      setError(null);

      try {
        const json = await apiFetch<ApiResponse>(`/actions/new-customer/${customerId}`);
        if (!mounted) return;

        setBranches(json.branches ?? []);
        setGenders(json.gender ?? []);
        setMaritals(json.marital ?? []);
        setLicenced(json.licenced ?? []);
        setKinRels(json.kinRelationship ?? []);
        setHereAboutUs(json.here_about_us ?? []);
        setGuarantors(json.guarantors ?? []);
        setGuarantorRelationship(json.gua_relationship ?? []);
        setCollateralAssets(json.collateral_assets ?? []);

        const c = json.customer;
        if (c && isEdit) {
          // ✅ edit mode: unlock all steps (remove this if you want lock even on edit)
          setMaxStepReached(steps.length - 1);

          setPersonal((p) => ({
            ...p,
            branchId: c.branch != null ? String(c.branch) : "",
            date: c.regDate ? String(c.regDate) : "",
            bdoId: c.bdo != null ? String(c.bdo) : "",
            dob: c.dob ? String(c.dob) : "",
            firstName: c.fname ?? "",
            middleName: c.mname ?? "",
            lastName: c.sname ?? "",
            aka: c.aka ?? "",
            gender: c.gender != null ? String(c.gender) : "",
            idNumber: c.national_id != null ? String(c.national_id) : "",
            phone: c.phone_number ?? "",
            altPhone: c.altPhoneNumber ?? "",
            email: c.email ?? "",
            marital: c.marital != null ? String(c.marital) : "",
            dependents: c.dependents_number != null ? String(c.dependents_number) : "",
            homeAddress: c.address ?? "",
            town: c.town ?? "",
            landmark: c.landmark ?? "",
            estate: c.village ?? "",
            houseNumber: c.house_number ?? "",
            ownership: c.ownership != null ? String(c.ownership) : "",
            rentAmount: c.rent_amount != null ? String(c.rent_amount) : "",
            rentSince: c.rent_since ?? "",
            assetValue: c.asset_value != null ? String(c.asset_value) : "",
            hearAbout: c.here_about_us != null ? String(c.here_about_us) : "",
            homeLat: c.home_latitude != null ? String(c.home_latitude) : "",
            homeLng: c.home_longitude != null ? String(c.home_longitude) : "",
          }));

          setBusiness((b) => ({
            ...b,
            name: c.businessName ?? "",
            town: c.businessTown ?? "",
            type: c.businessType ?? "",
            yearStarted: c.businessYearStarted != null ? String(c.businessYearStarted) : "",
            employees: c.businessNoEmployees != null ? String(c.businessNoEmployees) : "",
            buildingName: c.businessBuldingName ?? "",
            street: c.businessStreet ?? "",
            address: c.business_address ?? "",
            licensed: c.businessLicenced != null ? String(c.businessLicenced) : "",
            licenseNumber: c.businessLicenceNumber ?? "",
            currentStockValue: c.currentstockvalue != null ? String(c.currentstockvalue) : "",
            weeklySales: c.weeklysales != null ? String(c.weeklysales) : "",
            weeklyStockSpend: c.weeklyspendstock != null ? String(c.weeklyspendstock) : "",
            weeklyGrossProfit: c.weeklygrossprofit != null ? String(c.weeklygrossprofit) : "",
            weeklyExpenses: c.weeklyexpenses != null ? String(c.weeklyexpenses) : "",
            weeklyNetProfit: c.weeklynetprofit != null ? String(c.weeklynetprofit) : "",
            rentAmount: c.rentamount != null ? String(c.rentamount) : "",
            utilities: c.utilities != null ? String(c.utilities) : "",
            transport: c.transport != null ? String(c.transport) : "",
            weeklyChamaContribution: c.weeklychamacontribution != null ? String(c.weeklychamacontribution) : "",
            chamaMembers: c.nochamamembers != null ? String(c.nochamamembers) : "",
            chamaPayout: c.chamapayout != null ? String(c.chamapayout) : "",
            buildingOwnership: c.businessBldOwnership != null ? String(c.businessBldOwnership) : "",
            bizLat: c.business_latitude != null ? String(c.business_latitude) : "",
            bizLng: c.business_longitude != null ? String(c.business_longitude) : "",
          }));

          setKin((k) => ({
            ...k,
            name: c.kinName ?? "",
            phone: c.kinTelephone ?? "",
            town: c.kinTown ?? "",
            relationship: c.kinRelationship != null ? String(c.kinRelationship) : "",
          }));

          // ✅ existing photos from backend
          // prefer image_urls (recommended), else fallback to fields like c.photo_url, etc.
          const urls: ExistingPhotoUrls = {};

          // if your API returns: c.image_urls = { photo: "...", idside1: "...", ... }
          if (c?.image_urls) {
            photoSlots.forEach(({ key }) => {
              const u = c.image_urls?.[key];
              if (typeof u === "string" && u.length > 3) urls[key] = u;
            });
          }

          // optional: if you return photo_url directly
          if (!urls.photo && typeof c?.photo_url === "string") urls.photo = c.photo_url;

          setExistingPhotoUrls(urls);

          setAssessment((a) => ({
            ...a,
            stockNeat: c.stock_neat ?? "Yes",
            ledgerAccurate: c.accurate_ledger ?? "Yes",
            salesActivity: c.sales_activity ?? "Yes",
            permanent: c.permanent ?? "Yes",
            proofOwnership: c.proof_ownership ?? "Yes",
            forthComing: c.fourth_coming ?? "Yes",
            knownToMarket: c.known_market ?? "Yes",
            soundReputation: c.sound_reputation ?? "Yes",
          }));

          setFinalRemarks(c.remarks ?? "");

          // ✅ If backend returns guarantors, map into 5 slots (best effort)
          if (Array.isArray(json.guarantors) && json.guarantors.length) {
            const mapped: RefereeRow[] = Array.from({ length: REQUIRED_REFEREES }).map((_, i) => {
              const g = json.guarantors[i];
              return {
                id: uid(),
                name: g?.guarantor_name ?? "",
                relationship: g?.RELATIONSHIP ?? "",
                phone: g?.phone_number ?? "",
                idNumber: g?.national_id ?? "",
              };
            });
            setReferees(mapped);
          }
        } else {
          // ✅ create mode: lock to step 0
          setMaxStepReached(0);
        }

        // load BDOs if branch already known
        const branchToLoad = (json.customer?.branch != null ? String(json.customer.branch) : "") || "";
        if (branchToLoad) {
          setBdoLoading(true);
          try {
            const list = await apiFetch<Bdo[]>(`/actions/new-customer/branch-bdos/${branchToLoad}`);
            setBdos(list ?? []);
          } catch {
            setBdos([]);
          } finally {
            setBdoLoading(false);
          }
        }
      } catch (e: any) {
        if (!mounted) return;
        setError(e?.message ?? "Failed to load customer wizard");
      } finally {
        if (!mounted) return;
        setBootLoading(false);
      }
    }

    boot();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  useEffect(() => {
  const branchId = personal.branchId;

  if (!branchId) {
    setBdos([]);
    return;
  }

  let mounted = true;

  async function run() {
    setBdoLoading(true);
    try {
      const list = await apiFetch<Bdo[]>(`/actions/new-customer/branch-bdos/${branchId}`);
      if (!mounted) return;
      setBdos(list ?? []);
    } catch {
      if (!mounted) return;
      setBdos([]);
    } finally {
      if (!mounted) return;
      setBdoLoading(false);
    }
  }

  run();

  return () => {
    mounted = false;
  };
}, [personal.branchId]);

  const canPrev = step > 0;
  const canNext = step < steps.length - 1;
  const goPrev = () => setStep((s) => Math.max(0, s - 1));

  // required check
  const stepOk = useMemo(() => {
    const key = steps[step].key;

    if (key === "personal") {
      return (
        !!personal.branchId &&
        !!personal.date &&
        !!personal.bdoId &&
        helper.isAtLeast18(personal.dob) &&
        !!personal.firstName &&
        !!personal.lastName &&
        !!personal.gender &&
        !!personal.idNumber &&
        !!personal.phone &&
        !!personal.marital &&
        !!personal.ownership &&
        !!personal.hearAbout
      );
    }

    if (key === "business") return !!business.name && !!business.type;
    if (key === "kin") return !!kin.name && !!kin.phone && !!kin.town && !!kin.relationship;

    // ✅ MUST BE EXACTLY 5 referees, all filled
    if (key === "referees") {
      // return (
      //   referees.length === REQUIRED_REFEREES &&
      //   referees.every((r) => !!r.name && !!r.phone && !!r.relationship && !!r.idNumber)
      // );
      return true;
    }

    if (key === "final") return !!finalRemarks;

    return true;
  }, [step, personal, business, kin, referees, finalRemarks]);

  async function saveCurrentStep() {
    const stepKey = steps[step].key;
    const id = customerId;

    if (stepKey === "personal") {
      const payload = compact({
        fname: personal.firstName,
        mname: personal.middleName,
        sname: personal.lastName,
        aka: personal.aka,

        branch: toNumberOrNull(personal.branchId),
        bdo: toNumberOrNull(personal.bdoId),

        regDate: personal.date,
        dob: personal.dob,

        gender: toNumberOrNull(personal.gender),
        marital: toNumberOrNull(personal.marital),
        here_about_us: toNumberOrNull(personal.hearAbout),

        national_id: personal.idNumber,
        phone_number: personal.phone,
        altPhoneNumber: personal.altPhone,

        email: personal.email,
        address: personal.homeAddress,
        town: personal.town,
        landmark: personal.landmark,

        dependents_number: personal.dependents,
        ownership: personal.ownership,
        rent_amount: personal.rentAmount,
        rent_since: personal.rentSince,
        asset_value: personal.assetValue,
        village: personal.estate,
        house_number: personal.houseNumber,

        home_latitude: personal.homeLat,
        home_longitude: personal.homeLng,
      });

      const res = await postJson<{ ok: boolean; customer_id: number; message?: string }>(
        `/actions/new-customer/${id}/personal`,
        payload
      );
      if (!res?.ok) {
        toastError(res?.message || "Failed to save personal step");
        throw new Error(res?.message || "Failed to save personal step"); // stop next step
      }

      // ✅ success
      toastSuccess(res?.message || "Personal details saved successfully");

      if (id === "0") {
        nav(`/actions/new-customer/${res.customer_id}`, { replace: true });
        return { saved: true, newId: String(res.customer_id) };
      }

      return { saved: true };
    }

    if (stepKey === "business") {
      const payload = compact({
        businessName: business.name,
        businessType: business.type,
        businessTown: business.town,
        businessYearStarted: business.yearStarted,
        businessNoEmployees: business.employees,
        businessBuldingName: business.buildingName,
        businessStreet: business.street,
        business_address: business.address,
        businessLicenced: business.licensed,
        businessLicenceNumber: business.licenseNumber,
        currentstockvalue: business.currentStockValue,
        weeklysales: business.weeklySales,
        weeklyspendstock: business.weeklyStockSpend,
        weeklygrossprofit: business.weeklyGrossProfit,
        weeklyexpenses: business.weeklyExpenses,
        weeklynetprofit: business.weeklyNetProfit,
        rentamount: business.rentAmount,
        utilities: business.utilities,
        transport: business.transport,
        weeklychamacontribution: business.weeklyChamaContribution,
        nochamamembers: business.chamaMembers,
        chamapayout: business.chamaPayout,
        businessBldOwnership: business.buildingOwnership,
        business_latitude: business.bizLat,
        business_longitude: business.bizLng,
      });

      const res = await postJson<{ ok: boolean; message?: string }>(`/actions/new-customer/${id}/business`, payload);
      // if (!res?.ok) throw new Error(res?.message ?? "Failed to save business step");
      if (!res?.ok) {
        toastError(res?.message || "Failed to save business step");
        throw new Error(res?.message || "Failed to save business step"); // stop next step
      }

      // ✅ success
      toastSuccess(res?.message || "Business profile saved successfully");
      return { saved: true };
    }

    if (stepKey === "kin") {
      const payload = compact({
        kinName: kin.name,
        kinTelephone: kin.phone,
        kinTown: kin.town,
        kinRelationship: toNumberOrNull(kin.relationship),
      });

      const res = await postJson<{ ok: boolean; message?: string }>(`/actions/new-customer/${id}/kin`, payload);
      //if (!res?.ok) throw new Error(res?.message ?? "Failed to save kin step");
      if (!res?.ok) {
        toastError(res?.message || "Failed to save kin step");
        throw new Error(res?.message || "Failed to save kin step"); // stop next step
      }

      // ✅ success
      toastSuccess(res?.message || "Kin profile saved successfully");
      return { saved: true };
    }

    if (stepKey === "referees") {
      // ✅ guaranteed 5 by validation, but still safe:
      const rows = referees.slice(0, REQUIRED_REFEREES);

      const payload = {
        guaName: rows.map((r) => r.name),
        guaRelationship: rows.map((r) => r.relationship),
        guaPhoneNumber: rows.map((r) => r.phone),
        guaIdNumber: rows.map((r) => r.idNumber),
      };

      const res = await postJson<{ ok: boolean; message?: string }>(`/actions/new-customer/${id}/guarantors`, payload);
      // if (!res?.ok) throw new Error(res?.message ?? "Failed to save referees step");
      if (!res?.ok) {
        toastError(res?.message || "Failed to save referees step");
        throw new Error(res?.message || "Failed to save referees step"); // stop next step
      }

      // ✅ success
      toastSuccess(res?.message || "Referees saved successfully");
      return { saved: true };
    }

    if (stepKey === "assets") {
      const payload = {
        itemDesc: assets.map((a) => a.description),
        serialNo: assets.map((a) => a.serial),
        itemValue: assets.map((a) => a.value),
      };

      const res = await postJson<{ ok: boolean; message?: string }>(`/actions/new-customer/${id}/assets`, payload);
      //if (!res?.ok) throw new Error(res?.message ?? "Failed to save assets step");
      if (!res?.ok) {
        toastError(res?.message || "Failed to save assets step");
        throw new Error(res?.message || "Failed to save assets step"); // stop next step
      }

      // ✅ success
      toastSuccess(res?.message || "Assets saved successfully");
      return { saved: true };
    }

    if (stepKey === "photos") {
      const fd = new FormData();
      Object.entries(photos).forEach(([k, v]) => {
        if (v) fd.append(k, v);
      });

      const res = await apiFetch<{ ok: boolean; message?: string }>(`/actions/new-customer/${id}/files`, {
        method: "POST",
        body: fd,
      });

      //if (!res?.ok) throw new Error(res?.message ?? "Failed to save photos step");

      if (!res?.ok) {
        toastError(res?.message || "Failed to save photos step");
        throw new Error(res?.message || "Failed to save photos step"); // stop next step
      }

      // ✅ success
      toastSuccess(res?.message || "Photos saved successfully");
      return { saved: true };
    }

    if (stepKey === "assessment") {
      const payload = {
        stock_neat: assessment.stockNeat,
        accurate_ledger: assessment.ledgerAccurate,
        sales_activity: assessment.salesActivity,
        permanent: assessment.permanent,
        proof_ownership: assessment.proofOwnership,
        fourth_coming: assessment.forthComing,
        known_market: assessment.knownToMarket,
        sound_reputation: assessment.soundReputation,
      };

      const res = await postJson<{ ok: boolean; message?: string }>(`/actions/new-customer/${id}/assessment`, payload);
      // if (!res?.ok) throw new Error(res?.message ?? "Failed to save assessment step");
      if (!res?.ok) {
        toastError(res?.message || "Failed to save assessment step");
        throw new Error(res?.message || "Failed to save assessment step"); // stop next step
      }

      // ✅ success
      toastSuccess(res?.message || "Assessment saved successfully");
      return { saved: true };
    }

    if (stepKey === "final") {
      const payload = { remarks: finalRemarks };
      const res = await postJson<{ ok: boolean; message?: string }>(`/actions/new-customer/${id}/final`, payload);
      // if (!res?.ok) throw new Error(res?.message ?? "Failed to finalize customer");
      if (!res?.ok) {
        toastError(res?.message || "Failed to finalize customer");
        throw new Error(res?.message || "Failed to finalize customer"); // stop next step
      }

      // ✅ success
      toastSuccess(res?.message || "Profile saved successfully");
      return { saved: true, finished: true };
    }

    return { saved: true };
  }

  // inside your CustomerWizardPage component (top-level, before return)
  const maxDob = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setFullYear(d.getFullYear() - 18);
    return d;
  }, []);

  const dobError = personal.dob && !helper.isAtLeast18(personal.dob) ? "Customer must be at least 18 years old." : null;

  const onSaveNext = async () => {
    if (!stepOk) return;

    setSaving(true);
    setError(null);
    try {
      const result = await saveCurrentStep();
      if (result?.finished) return;

      // ✅ unlock next step ONLY after save
      setMaxStepReached((prev) => Math.max(prev, step + 1));

      if (step < steps.length - 1) setStep((s) => Math.min(steps.length - 1, s + 1));
    } catch (e: any) {
      setError(e?.message ?? "Failed to save step");
    } finally {
      setSaving(false);
    }
  };

  if (bootLoading) {
    return (
      <div className="w-full max-w-6xl mx-auto px-3 sm:px-6 py-10">
        <div className="rounded-xl border border-border bg-card p-6 flex items-center gap-3">
          <Loader2 className="h-5 w-5 animate-spin" />
          <div>
            <div className="font-semibold">Loading customer wizard…</div>
            <div className="text-sm text-muted-foreground">Fetching branches, gender, BDOs, and customer data.</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-6xl mx-auto px-3 sm:px-6 py-4 space-y-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{isEdit ? "Edit Customer" : "Add Customer"}</h1>
          <p className="text-sm text-muted-foreground">{isEdit ? `Editing customer #${customerId}` : "Create new customer."}</p>
        </div>

        <Badge variant="secondary" className="hidden lg:inline-flex">
          Step {step + 1} of {steps.length}
        </Badge>
      </div>

      {error && <div className="rounded-xl border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">{error}</div>}

      <ResponsiveTabs
        step={step}
        setStep={(n) => {
          if (saving) return;
          if (n <= maxStepReached) setStep(n);
        }}
        stepOk={stepOk}
        maxStepReached={maxStepReached}
        saving={saving}
      />

      <AnimatePresence mode="wait">
        <motion.div
          key={steps[step].key}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.18 }}
          className="space-y-5"
        >
          {/* PERSONAL */}
          {steps[step].key === "personal" && (
            <SectionShell title="Personal Information" subtitle="Select Branch first, then choose BDO (loaded per branch).">
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-5">
                <div className="space-y-4">
                  <Field label="Branch" required>
                    <Select
                      value={personal.branchId}
                      onValueChange={(v) =>
                        setPersonal((p) => ({
                          ...p,
                          branchId: v,
                          bdoId: "", // clear only when user changes branch
                        }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select branch" />
                      </SelectTrigger>
                      <SelectContent>
                        {branches.map((b) => (
                          <SelectItem key={b.id} value={String(b.id)}>
                            {b.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>

                  <Field label="BDO" required hint={!personal.branchId ? "Select branch first" : bdoLoading ? "Loading BDO list…" : undefined}>
                    <Select
                      value={personal.bdoId}
                      onValueChange={(v) => setPersonal((p) => ({ ...p, bdoId: v }))}
                      disabled={!personal.branchId || bdoLoading}
                    >
                      <SelectTrigger className={cn((!personal.branchId || bdoLoading) && "opacity-60 cursor-not-allowed")}>
                        <SelectValue placeholder={!personal.branchId ? "Select branch first" : "Select BDO"} />
                      </SelectTrigger>
                      <SelectContent>
                        {bdos.map((b) => (
                          <SelectItem key={b.id} value={String(b.id)}>
                            {b.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>

                  <Field label="First Name" required>
                    <Input placeholder="e.g. John" value={personal.firstName} onChange={(e) => setPersonal((p) => ({ ...p, firstName: e.target.value }))} />
                  </Field>

                  <Field label="Middle Name">
                    <Input placeholder="e.g. Mwangi" value={personal.middleName} onChange={(e) => setPersonal((p) => ({ ...p, middleName: e.target.value }))} />
                  </Field>

                  <Field label="Last Name" required>
                    <Input placeholder="e.g. Doe" value={personal.lastName} onChange={(e) => setPersonal((p) => ({ ...p, lastName: e.target.value }))} />
                  </Field>

                  <Field label="AKA">
                    <Input placeholder="Optional nickname" value={personal.aka} onChange={(e) => setPersonal((p) => ({ ...p, aka: e.target.value }))} />
                  </Field>

                  <Field label="Dependents">
                    <Input placeholder="e.g. 0" value={personal.dependents} onChange={(e) => setPersonal((p) => ({ ...p, dependents: e.target.value }))} />
                  </Field>
                </div>

                <div className="space-y-4">
                  <Field label="Registration date" required>
                    <DatePickerField value={personal.date} onChange={(v) => setPersonal((p) => ({ ...p, date: v }))} placeholder="Registration date" />
                  </Field>

                  <Field label="DOB" required>
                    <DatePickerField
                      value={personal.dob}
                      onChange={(v) => setPersonal((p) => ({ ...p, dob: v }))}
                      placeholder="Pick date of birth"
                      max={maxDob}                 // disables anything after maxDob
                      defaultMonth={maxDob}
                      error={dobError}
                    />
                  </Field>




                  <Field label="Gender" required>
                    <Select value={personal.gender} onValueChange={(v) => setPersonal((p) => ({ ...p, gender: v }))}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select gender" />
                      </SelectTrigger>
                      <SelectContent>
                        {genders.map((g) => (
                          <SelectItem key={g.id} value={String(g.id)}>
                            {g.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>

                  <Field label="Marital Status" required>
                    <Select value={personal.marital} onValueChange={(v) => setPersonal((p) => ({ ...p, marital: v }))}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select marital status" />
                      </SelectTrigger>
                      <SelectContent>
                        {maritals.map((m) => (
                          <SelectItem key={m.id} value={String(m.id)}>
                            {m.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>

                  <Field label="ID Number" required>
                    <Input placeholder="National ID" value={personal.idNumber} onChange={(e) => setPersonal((p) => ({ ...p, idNumber: e.target.value }))} />
                  </Field>

                  <Field
                    label="Phone Number"
                    required
                    hint={isEdit ? "Locked" : undefined}
                  >
                    <div className="relative">
                      <Input
                        placeholder="e.g. 07XXXXXXXX"
                        value={personal.phone}
                        readOnly={isEdit}
                        onChange={isEdit ? undefined : (e) => setPersonal((p) => ({ ...p, phone: e.target.value }))}
                        className={cn(isEdit && "bg-muted/40 pr-12")}
                      />
                      {isEdit ? (
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                          🔒
                        </span>
                      ) : null}
                    </div>
                  </Field>

                  <Field label="Alt Phone Number">
                    <Input placeholder="Optional" value={personal.altPhone} onChange={(e) => setPersonal((p) => ({ ...p, altPhone: e.target.value }))} />
                  </Field>


                </div>

                <div className="space-y-4">
                  <Field label="Email">
                    <Input placeholder="e.g. name@example.com" value={personal.email} onChange={(e) => setPersonal((p) => ({ ...p, email: e.target.value }))} />
                  </Field>

                  <Field label="Home Address">
                    <Textarea
                      placeholder="Area / street / plot / directions"
                      value={personal.homeAddress}
                      onChange={(e) => setPersonal((p) => ({ ...p, homeAddress: e.target.value }))}
                      className="min-h-[112px]"
                    />
                  </Field>

                  <Field label="Town">
                    <Input placeholder="e.g. Nairobi" value={personal.town} onChange={(e) => setPersonal((p) => ({ ...p, town: e.target.value }))} />
                  </Field>

                  <Field label="Landmark">
                    <Input
                      placeholder="e.g. Near ABC Church"
                      value={personal.landmark}
                      onChange={(e) => setPersonal((p) => ({ ...p, landmark: e.target.value }))}
                    />
                  </Field>

                  <Field label="Village / Estate">
                    <Input placeholder="e.g. Umoja / Ruaka" value={personal.estate} onChange={(e) => setPersonal((p) => ({ ...p, estate: e.target.value }))} />
                  </Field>

                  <Field label="House Number">
                    <Input placeholder="e.g. Hse 12 / Plot 5" value={personal.houseNumber} onChange={(e) => setPersonal((p) => ({ ...p, houseNumber: e.target.value }))} />
                  </Field>


                  <Field label="Ownership" required>
                    <select
                      value={personal.ownership}
                      onChange={(e) =>
                        setPersonal((p) => ({ ...p, ownership: e.target.value }))
                      }
                      className="h-11 w-full rounded-xl border border-border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-ring"
                    >
                      <option value="">Select ownership</option>
                      <option value="Home Owner">Home Owner</option>
                      <option value="Rental">Rental</option>
                      <option value="Family">Family</option>
                      <option value="Other">Other</option>
                    </select>
                  </Field>

                </div>

                <div className="space-y-4">

                  {/* ✅ Show only if Rental */}
                  {personal.ownership === "Rental" && (
                    <>
                      <Field label="Rent Amount">
                        <Input
                          placeholder="e.g. 8000"
                          value={personal.rentAmount}
                          onChange={(e) =>
                            setPersonal((p) => ({ ...p, rentAmount: e.target.value }))
                          }
                        />
                      </Field>

                      <Field label="Rent Since">
                        <Input
                          placeholder="YYYY-MM"
                          value={personal.rentSince}
                          onChange={(e) =>
                            setPersonal((p) => ({ ...p, rentSince: e.target.value }))
                          }
                        />
                      </Field>
                    </>
                  )}

                  <Field label="Asset Value">
                    <Input placeholder="e.g. 50000" value={personal.assetValue} onChange={(e) => setPersonal((p) => ({ ...p, assetValue: e.target.value }))} />
                  </Field>

                  <Field label="Hear About Us" required>
                    <Select value={personal.hearAbout} onValueChange={(v) => setPersonal((p) => ({ ...p, hearAbout: v }))}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        {hereAboutUs.map((h) => (
                          <SelectItem key={h.id} value={String(h.id)}>
                            {h.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>

                  <div className="flex items-center justify-between gap-2 rounded-xl border border-border bg-muted/20 p-3">
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-primary" />
                      <div>
                        <p className="text-sm font-medium">Pick Home Location</p>
                        <p className="text-xs text-muted-foreground">Optional GPS capture</p>
                      </div>
                    </div>
                    <Button type="button" variant="outline" size="sm" onClick={() => setPersonal((p) => ({ ...p, homeLat: "-1.29", homeLng: "36.82" }))}>
                      Pick
                    </Button>
                  </div>

                  <Field label="Home Latitude">
                    <Input placeholder="Optional" value={personal.homeLat} onChange={(e) => setPersonal((p) => ({ ...p, homeLat: e.target.value }))} />
                  </Field>

                  <Field label="Home Longitude">
                    <Input placeholder="Optional" value={personal.homeLng} onChange={(e) => setPersonal((p) => ({ ...p, homeLng: e.target.value }))} />
                  </Field>
                </div>
              </div>
            </SectionShell>
          )}

          {/* BUSINESS */}
          {steps[step].key === "business" && (
            <SectionShell title="Business Information">
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-5">
                <div className="space-y-4">
                  <Field label="Business Name" required>
                    <Input placeholder="e.g. Wanjiku Grocery" value={business.name} onChange={(e) => setBusiness((p) => ({ ...p, name: e.target.value }))} />
                  </Field>

                  <Field label="Type" required>
                    <Input
                      placeholder="Retail / Wholesale / Services…"
                      value={business.type}
                      onChange={(e) => setBusiness((p) => ({ ...p, type: e.target.value }))}
                    />
                  </Field>

                  <Field label="Town">
                    <Input placeholder="e.g. Nairobi" value={business.town} onChange={(e) => setBusiness((p) => ({ ...p, town: e.target.value }))} />
                  </Field>

                  <Field label="Street">
                    <Input placeholder="e.g. Moi Avenue" value={business.street} onChange={(e) => setBusiness((p) => ({ ...p, street: e.target.value }))} />
                  </Field>

                  <Field label="Building Name">
                    <Input placeholder="e.g. ABC Plaza" value={business.buildingName} onChange={(e) => setBusiness((p) => ({ ...p, buildingName: e.target.value }))} />
                  </Field>

                  <Field label="Year Started">
                    <Input placeholder="e.g. 2019" value={business.yearStarted} onChange={(e) => setBusiness((p) => ({ ...p, yearStarted: e.target.value }))} />
                  </Field>

                  <Field label="No of Employees">
                    <Input placeholder="e.g. 3" value={business.employees} onChange={(e) => setBusiness((p) => ({ ...p, employees: e.target.value }))} />
                  </Field>
                </div>

                <div className="space-y-4">




                  <Field label="Licensed">
                    <Select value={business.licensed} onValueChange={(v) => setBusiness((p) => ({ ...p, licensed: v }))}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        {licenced.map((m) => (
                          <SelectItem key={m.id} value={String(m.id)}>
                            {m.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>

                  <Field label="License Number">
                    <Input placeholder="Optional" value={business.licenseNumber} onChange={(e) => setBusiness((p) => ({ ...p, licenseNumber: e.target.value }))} />
                  </Field>

                  <Field label="Current Stock Value">
                    <Input
                      placeholder="KES"
                      value={business.currentStockValue}
                      onChange={(e) => setBusiness((p) => ({ ...p, currentStockValue: e.target.value }))}
                    />
                  </Field>

                  <Field label="Weekly Sales">
                    <Input placeholder="KES" value={business.weeklySales} onChange={(e) => setBusiness((p) => ({ ...p, weeklySales: e.target.value }))} />
                  </Field>

                  <Field label="Weekly Spend on Stock">
                    <Input
                      placeholder="KES"
                      value={business.weeklyStockSpend}
                      onChange={(e) => setBusiness((p) => ({ ...p, weeklyStockSpend: e.target.value }))}
                    />
                  </Field>

                  <Field label="Weekly Gross Profit">
                    <Input
                      placeholder="KES"
                      value={business.weeklyGrossProfit}
                      onChange={(e) => setBusiness((p) => ({ ...p, weeklyGrossProfit: e.target.value }))}
                    />
                  </Field>
                </div>

                <div className="space-y-4">




                  <Field label="Weekly Expenses">
                    <Input placeholder="KES" value={business.weeklyExpenses} onChange={(e) => setBusiness((p) => ({ ...p, weeklyExpenses: e.target.value }))} />
                  </Field>

                  <Field label="Weekly Net Profit">
                    <Input placeholder="KES" value={business.weeklyNetProfit} onChange={(e) => setBusiness((p) => ({ ...p, weeklyNetProfit: e.target.value }))} />
                  </Field>

                  <Field label="Rent Amount">
                    <Input placeholder="KES" value={business.rentAmount} onChange={(e) => setBusiness((p) => ({ ...p, rentAmount: e.target.value }))} />
                  </Field>

                  <Field label="Utilities">
                    <Input placeholder="KES" value={business.utilities} onChange={(e) => setBusiness((p) => ({ ...p, utilities: e.target.value }))} />
                  </Field>

                  <Field label="Business Address">
                    <Textarea
                      placeholder="Full business address / directions"
                      value={business.address}
                      onChange={(e) => setBusiness((p) => ({ ...p, address: e.target.value }))}
                      className="min-h-[120px]"
                    />
                  </Field>
                  <Field label="Transport">
                    <Input placeholder="KES" value={business.transport} onChange={(e) => setBusiness((p) => ({ ...p, transport: e.target.value }))} />
                  </Field>
                </div>

                <div className="space-y-4">




                  <Field label="Weekly Chama Contribution">
                    <Input
                      placeholder="KES"
                      value={business.weeklyChamaContribution}
                      onChange={(e) => setBusiness((p) => ({ ...p, weeklyChamaContribution: e.target.value }))}
                    />
                  </Field>

                  <Field label="No of Chama Members">
                    <Input placeholder="e.g. 10" value={business.chamaMembers} onChange={(e) => setBusiness((p) => ({ ...p, chamaMembers: e.target.value }))} />
                  </Field>

                  <Field label="Chama Payout">
                    <Input placeholder="KES" value={business.chamaPayout} onChange={(e) => setBusiness((p) => ({ ...p, chamaPayout: e.target.value }))} />
                  </Field>

                  <div className="flex items-center justify-between gap-2 rounded-xl border border-border bg-muted/20 p-3">
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-primary" />
                      <div>
                        <p className="text-sm font-medium">Pick Business Location</p>
                        <p className="text-xs text-muted-foreground">Optional GPS capture</p>
                      </div>
                    </div>
                    <Button type="button" variant="outline" size="sm" onClick={() => setBusiness((p) => ({ ...p, bizLat: "-1.29", bizLng: "36.82" }))}>
                      Pick
                    </Button>
                  </div>

                  <Field label="Business Latitude">
                    <Input placeholder="Optional" value={business.bizLat} onChange={(e) => setBusiness((p) => ({ ...p, bizLat: e.target.value }))} />
                  </Field>

                  <Field label="Business Longitude">
                    <Input placeholder="Optional" value={business.bizLng} onChange={(e) => setBusiness((p) => ({ ...p, bizLng: e.target.value }))} />
                  </Field>
                </div>
              </div>
            </SectionShell>
          )}

          {/* KIN */}
          {steps[step].key === "kin" && (
            <SectionShell title="Next of Kin">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <Field label="Name of Kin" required>
                  <Input placeholder="Full name" value={kin.name} onChange={(e) => setKin((p) => ({ ...p, name: e.target.value }))} />
                </Field>
                <Field label="Telephone" required>
                  <Input placeholder="07XXXXXXXX" value={kin.phone} onChange={(e) => setKin((p) => ({ ...p, phone: e.target.value }))} />
                </Field>
                <Field label="Town" required>
                  <Input placeholder="Town" value={kin.town} onChange={(e) => setKin((p) => ({ ...p, town: e.target.value }))} />
                </Field>
              </div>

              <div className="max-w-sm">
                <Field label="Relationship" required>
                  <Select value={kin.relationship} onValueChange={(v) => setKin((p) => ({ ...p, relationship: v }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select relationship" />
                    </SelectTrigger>
                    <SelectContent>
                      {kinRels.map((r) => (
                        <SelectItem key={r.id} value={String(r.id)}>
                          {r.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              </div>
            </SectionShell>
          )}

          {/* REFEREES (EXACTLY 5, VALIDATED) */}
          {steps[step].key === "referees" && (
           <SectionShell
  title="Referees (Guarantors)"
  subtitle="Optional. You can skip this step or fill referee details if available."
>
              {/* Top bar */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant={referees.length === REQUIRED_REFEREES ? "secondary" : "destructive"}>
                    {referees.length}/{REQUIRED_REFEREES}
                  </Badge>
                  <p className="text-sm text-muted-foreground">Fill all fields for each referee.</p>
                </div>

                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    disabled
                    title={`Locked: referees must be exactly ${REQUIRED_REFEREES}`}
                    onClick={() => { }}
                  >
                    <Plus className="h-4 w-4 mr-1" /> Add
                  </Button>
                </div>
              </div>

              {!stepOk ? (
                <div className="rounded-xl border border-blue-500/30 bg-blue-500/10 p-3 text-sm text-blue-700 dark:text-blue-300">
  Referees are optional. You may leave them blank and continue.
</div>
              ) : null}

              {/* ✅ MOBILE (cards) */}
              <div className="grid grid-cols-1 gap-3 md:hidden">
                {referees.map((r, idx) => (
                  <div key={r.id} className="rounded-xl border border-border bg-card p-4 space-y-3">
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-sm font-semibold">Referee {idx + 1}</div>
                      <Button type="button" variant="destructive" size="sm" disabled title={`Locked: must keep exactly ${REQUIRED_REFEREES}`}>
                        <Trash2 className="h-4 w-4 mr-1" />
                        Delete
                      </Button>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <Label className="text-xs text-muted-foreground">Name</Label>
                        <Input
                          placeholder="Full name"
                          value={r.name}
                          onChange={(e) =>
                            setReferees((rows) => rows.map((x) => (x.id === r.id ? { ...x, name: e.target.value } : x)))
                          }
                        />
                      </div>

                      <div className="space-y-1.5">
                        <Label className="text-xs text-muted-foreground">Relationship</Label>
                        {/* <Input
              placeholder="e.g. Brother / Friend"
              value={r.relationship}
              onChange={(e) =>
                setReferees((rows) => rows.map((x) => (x.id === r.id ? { ...x, relationship: e.target.value } : x)))
              }
            /> */}

                        <Select
                          value={r.relationship}
                          onValueChange={(v) =>
                            setReferees((rows) => rows.map((x) => (x.id === r.id ? { ...x, relationship: v } : x)))
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select relationship" />
                          </SelectTrigger>
                          <SelectContent>
                            {guarantorRelationship.map((g) => (
                              <SelectItem key={g.id} value={String(g.id)}>
                                {g.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>

                      </div>

                      <div className="space-y-1.5">
                        <Label className="text-xs text-muted-foreground">Phone</Label>
                        <Input
                          placeholder="07XXXXXXXX"
                          value={r.phone}
                          onChange={(e) =>
                            setReferees((rows) => rows.map((x) => (x.id === r.id ? { ...x, phone: e.target.value } : x)))
                          }
                        />
                      </div>

                      <div className="space-y-1.5">
                        <Label className="text-xs text-muted-foreground">ID Number</Label>
                        <Input
                          placeholder="National ID"
                          value={r.idNumber}
                          onChange={(e) =>
                            setReferees((rows) => rows.map((x) => (x.id === r.id ? { ...x, idNumber: e.target.value } : x)))
                          }
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* ✅ TABLET + DESKTOP (grid table) */}
              <div className="hidden md:block overflow-x-auto rounded-xl border border-border">
                {/* iPad/tablet: allow scroll if needed, desktop: fits */}
                <div className="min-w-[900px] lg:min-w-0">
                  <div className="grid grid-cols-5 gap-3 bg-muted/40 px-4 py-3 text-sm font-semibold">
                    <div>Name</div>
                    <div>Relationship</div>
                    <div>Phone</div>
                    <div>ID Number</div>
                    <div className="text-right">Action</div>
                  </div>
                  <Separator />

                  {referees.map((r, idx) => (
                    <div key={r.id}>
                      <div className="grid grid-cols-5 gap-3 px-4 py-3 items-center">
                        <Input
                          placeholder="Full name"
                          value={r.name}
                          onChange={(e) =>
                            setReferees((rows) => rows.map((x) => (x.id === r.id ? { ...x, name: e.target.value } : x)))
                          }
                        />
                        <Select
                          value={r.relationship}
                          onValueChange={(v) =>
                            setReferees((rows) => rows.map((x) => (x.id === r.id ? { ...x, relationship: v } : x)))
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select relationship" />
                          </SelectTrigger>
                          <SelectContent>
                            {guarantorRelationship.map((g) => (
                              <SelectItem key={g.id} value={String(g.id)}>
                                {g.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Input
                          placeholder="07XXXXXXXX"
                          value={r.phone}
                          onChange={(e) =>
                            setReferees((rows) => rows.map((x) => (x.id === r.id ? { ...x, phone: e.target.value } : x)))
                          }
                        />
                        <Input
                          placeholder="National ID"
                          value={r.idNumber}
                          onChange={(e) =>
                            setReferees((rows) => rows.map((x) => (x.id === r.id ? { ...x, idNumber: e.target.value } : x)))
                          }
                        />
                        <div className="flex justify-end">
                          <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            disabled
                            title={`Locked: must keep exactly ${REQUIRED_REFEREES}`}
                            onClick={() => { }}
                          >
                            <Trash2 className="h-4 w-4 mr-1" />
                            Delete
                          </Button>
                        </div>
                      </div>

                      {/* row separator */}
                      {idx !== referees.length - 1 ? <Separator /> : null}
                    </div>
                  ))}
                </div>
              </div>
            </SectionShell>
          )}

          {/* ASSETS */}
          {steps[step].key === "assets" && (
            <SectionShell title="Customer Assets" subtitle="Saves arrays to /assets endpoint.">
              {/* Top bar */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <Button
                  type="button"
                  onClick={() => setAssets((rows) => [...rows, { id: uid(), description: "", serial: "", value: "" }])}
                  className="w-full sm:w-auto"
                >
                  <Plus className="h-4 w-4 mr-1" /> Add
                </Button>

                <p className="text-sm text-muted-foreground">
                  Mobile uses cards • iPad/desktop uses table
                </p>
              </div>

              {/* ✅ MOBILE (cards) */}
              <div className="grid grid-cols-1 gap-3 md:hidden">
                {assets.map((a, idx) => (
                  <div key={a.id} className="rounded-xl border border-border bg-card p-4 space-y-3">
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-sm font-semibold">Asset {idx + 1}</div>
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        onClick={() => setAssets((rows) => rows.filter((x) => x.id !== a.id))}
                        disabled={assets.length === 1}
                        title={assets.length === 1 ? "At least one asset is required" : "Remove asset"}
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Remove
                      </Button>
                    </div>

                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground">Item Description</Label>
                      <Input
                        placeholder="e.g. TV / Motorbike"
                        value={a.description}
                        onChange={(e) =>
                          setAssets((rows) => rows.map((x) => (x.id === a.id ? { ...x, description: e.target.value } : x)))
                        }
                      />
                    </div>

                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground">Serial Number</Label>
                      <Input
                        placeholder="Serial / Plate / Ref"
                        value={a.serial}
                        onChange={(e) =>
                          setAssets((rows) => rows.map((x) => (x.id === a.id ? { ...x, serial: e.target.value } : x)))
                        }
                      />
                    </div>

                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground">Value (KES)</Label>
                      <Input
                        placeholder="Value (KES)"
                        value={a.value}
                        onChange={(e) =>
                          setAssets((rows) => rows.map((x) => (x.id === a.id ? { ...x, value: e.target.value } : x)))
                        }
                      />
                    </div>
                  </div>
                ))}
              </div>

              {/* ✅ iPad + Desktop (table/grid) */}
              <div className="hidden md:block overflow-x-auto rounded-xl border border-border">
                {/* iPad Air: allow scrolling; Desktop: fits normally */}
                <div className="min-w-[860px] lg:min-w-0">
                  <div className="grid grid-cols-12 gap-3 bg-muted/40 px-4 py-3 text-sm font-semibold">
                    <div className="col-span-5">Item Description</div>
                    <div className="col-span-3">Serial Number</div>
                    <div className="col-span-3">Value</div>
                    <div className="col-span-1 text-right">Action</div>
                  </div>
                  <Separator />

                  {assets.map((a, idx) => (
                    <div key={a.id}>
                      <div className="grid grid-cols-12 gap-3 px-4 py-3 items-center">
                        <div className="col-span-5">
                          <Input
                            placeholder="e.g. TV / Motorbike"
                            value={a.description}
                            onChange={(e) =>
                              setAssets((rows) => rows.map((x) => (x.id === a.id ? { ...x, description: e.target.value } : x)))
                            }
                          />
                        </div>

                        <div className="col-span-3">
                          <Input
                            placeholder="Serial / Plate / Ref"
                            value={a.serial}
                            onChange={(e) =>
                              setAssets((rows) => rows.map((x) => (x.id === a.id ? { ...x, serial: e.target.value } : x)))
                            }
                          />
                        </div>

                        <div className="col-span-3">
                          <Input
                            placeholder="Value (KES)"
                            value={a.value}
                            onChange={(e) =>
                              setAssets((rows) => rows.map((x) => (x.id === a.id ? { ...x, value: e.target.value } : x)))
                            }
                          />
                        </div>

                        <div className="col-span-1 flex justify-end">
                          <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            onClick={() => setAssets((rows) => rows.filter((x) => x.id !== a.id))}
                            disabled={assets.length === 1}
                            title={assets.length === 1 ? "At least one asset is required" : "Remove asset"}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>

                      {idx !== assets.length - 1 ? <Separator /> : null}
                    </div>
                  ))}
                </div>
              </div>
            </SectionShell>
          )}

          {/* PHOTOS */}
          {steps[step].key === "photos" && (
            <SectionShell title="Uploads" subtitle="Uploads match backend keys exactly (multipart/form-data).">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {photoSlots.map((slot) => (
                  <PhotoTile
                    key={slot.key}
                    label={slot.label}
                    value={photos[slot.key]}
                    existingUrl={existingPhotoUrls[slot.key] ?? null}
                    onPick={(f) => setPhotos((p) => ({ ...p, [slot.key]: f }))}
                    onClear={() => setPhotos((p) => ({ ...p, [slot.key]: null }))}
                  />
                ))}
              </div>

              <div className="rounded-xl border border-border bg-muted/20 p-4 flex items-start gap-3">
                <ShieldCheck className="h-5 w-5 text-primary mt-0.5" />
                <div>
                  <p className="text-sm font-medium">Tip</p>
                  <p className="text-sm text-muted-foreground">
                    This step calls <b>/actions/new-customer/{customerId}/files</b> using <b>multipart/form-data</b>.
                  </p>
                </div>
              </div>
            </SectionShell>
          )}

          {/* ASSESSMENT */}
          {steps[step].key === "assessment" && (
            <SectionShell title="Customer Assessment Information">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {[
                  { k: "stockNeat", label: "Stock Neat" },
                  { k: "ledgerAccurate", label: "Accurate Ledger Book" },
                  { k: "salesActivity", label: "Sales Activity" },
                  { k: "permanent", label: "Permanent" },
                  { k: "proofOwnership", label: "Proof of Ownership" },
                  { k: "forthComing", label: "Fourth Coming" },
                  { k: "knownToMarket", label: "Known to Market authorities" },
                  { k: "soundReputation", label: "Sound Reputation" },
                ].map((row) => (
                  <div key={row.k} className="space-y-2 rounded-xl border border-border bg-card p-4">
                    <Label className="text-sm font-medium">
                      {row.label} <span className="text-destructive">*</span>
                    </Label>

                    <RadioGroup
                      value={(assessment as any)[row.k]}
                      onValueChange={(v) => setAssessment((p) => ({ ...p, [row.k]: v } as any))}
                      className="flex items-center gap-6"
                    >
                      {yesNo.map((v) => (
                        <div key={v} className="flex items-center gap-2">
                          <RadioGroupItem value={v} id={`${row.k}-${v}`} />
                          <Label htmlFor={`${row.k}-${v}`}>{v}</Label>
                        </div>
                      ))}
                    </RadioGroup>
                  </div>
                ))}
              </div>
            </SectionShell>
          )}

          {/* FINAL */}
          {steps[step].key === "final" && (
            <SectionShell title="Customer Final Step">
              <Field label="Remarks" required>
                <Textarea
                  placeholder="Add remarks, risks, notes, recommendations..."
                  value={finalRemarks}
                  onChange={(e) => setFinalRemarks(e.target.value)}
                  className="min-h-[220px]"
                />
              </Field>
            </SectionShell>
          )}
        </motion.div>
      </AnimatePresence>

      <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-between gap-3 pt-2">
        <Button type="button" variant="outline" className="rounded-xl" onClick={goPrev} disabled={!canPrev || saving}>
          <ChevronLeft className="h-4 w-4 mr-1" />
          Previous
        </Button>

        <div className="flex items-center justify-between sm:justify-end gap-2">
          {!stepOk ? <p className="text-sm text-destructive mr-2 hidden md:block">Please fill all required fields (*) before continuing.</p> : null}

          <Button type="button" className="rounded-xl" onClick={onSaveNext} disabled={!stepOk || saving}>
            {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
            {canNext ? "Save & Next" : isEdit ? "Update & Finish" : "Save & Finish"}
            {canNext ? <ChevronRight className="h-4 w-4 ml-1" /> : null}
          </Button>
        </div>
      </div>
    </div>
  );
}